import { ReactNode, useState, useEffect, useRef, useCallback, Children } from "react";
import { twMerge } from "tailwind-merge";
import useContainerWidthBreakpoint from "@core/hooks/useContainerWidthBreakpoint";
import makeVideoTileClassName from "../../utils/makeVideoTileClassName";
import { PaginationDots } from "@themes/pollinator/ui/PaginationDots";

type Props = {
  size: number;
  children: ReactNode;
  className?: string;
  isContentShare?: boolean;
};

export function VideoMeetingGrid({
  size,
  children,
  className,
  isContentShare,
  ...rest
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  const { containerHeight, isXs, isSm, isMd, isLg, isXl, is2xl } =
    useContainerWidthBreakpoint(ref);

  const childrenArray = Children.toArray(children);
  const gridSize = size;
  const mobileColumnSize = 2;
  const totalGroups = Math.ceil(gridSize / mobileColumnSize);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || isXs) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.clientWidth;
      const currentIndex = Math.round(scrollLeft / containerWidth);
      setCurrentGroupIndex(Math.min(currentIndex, totalGroups - 1));
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [isSm, totalGroups]);

  // Keyboard navigation: Left/Right arrow keys to navigate between tile pages
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (totalGroups <= 1) return;
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      let nextIndex = currentGroupIndex;
      if (e.key === "ArrowRight") {
        nextIndex = Math.min(currentGroupIndex + 1, totalGroups - 1);
      } else if (e.key === "ArrowLeft") {
        nextIndex = Math.max(currentGroupIndex - 1, 0);
      } else {
        return;
      }

      e.preventDefault();
      const targetScrollLeft = nextIndex * scrollContainer.clientWidth;
      scrollContainer.scrollTo({ left: targetScrollLeft, behavior: "smooth" });
      setCurrentGroupIndex(nextIndex);
    },
    [currentGroupIndex, totalGroups]
  );

  return (
    <>
      <div
        ref={ref}
        className={twMerge(
          "flex flex-col w-full h-full max-w-screen-4xl overflow-hidden",
          !isXs && "relative",
          isContentShare && "h-auto",
          className
        )}
        onKeyDown={handleGridKeyDown}
        tabIndex={totalGroups > 1 && !isXs ? 0 : undefined}
        role={totalGroups > 1 && !isXs ? "region" : undefined}
        aria-label={totalGroups > 1 && !isXs ? `Video grid, page ${currentGroupIndex + 1} of ${totalGroups}` : undefined}
        {...rest}
      >
        <div
          className={twMerge(
            "flex flex-row flex-wrap justify-start items-start content-start w-full h-full",
            "sm:justify-center sm:items-center sm:content-center",
            isContentShare &&
              "flex-nowrap basis-36 sm:justify-start mb-4 h-auto overflow-x-auto"
          )}
        >
          {(() => {
            const renderTile = (child: ReactNode, index: number) => (
              <div
                key={index}
                className={makeVideoTileClassName({
                  gridSize,
                  containerHeight,
                  isXs,
                  isSm,
                  isMd,
                  isLg,
                  isXl,
                  is2xl,
                  isContentShare: isContentShare || false,
                })}
              >
                {child}
              </div>
            );

            if (!isXs && !isContentShare) {
              return (
                <div
                  ref={scrollContainerRef}
                  className="flex flex-row w-full h-full overflow-x-auto gap-4 pb-2 scroll-smooth snap-x snap-mandatory"
                >
                  {Array.from({ length: totalGroups }).map((_, groupIndex) => (
                    <div
                      key={`group-${groupIndex}`}
                      className="flex flex-col flex-shrink-0 h-full w-full min-w-full snap-start"
                    >
                      {childrenArray
                        .slice(
                          groupIndex * mobileColumnSize,
                          (groupIndex + 1) * mobileColumnSize
                        )
                        .map((child, itemIndex) => {
                          const index =
                            groupIndex * mobileColumnSize + itemIndex;
                          return renderTile(child, index);
                        })}
                    </div>
                  ))}
                </div>
              );
            } else {
              return childrenArray.map((child, index) =>
                renderTile(child, index)
              );
            }
          })()}
        </div>
      </div>
      {!isXs && totalGroups > 1 && !isContentShare && (
        <PaginationDots
          totalPages={totalGroups}
          currentPageIndex={currentGroupIndex}
          scrollContainerRef={scrollContainerRef}
        />
      )}
    </>
  );
}
